<template>
  <div class="p-field" :class="{ 'p-float-label': labelStyle === 'float' }">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- Textarea Wrapper -->
      <div :class="{ 'p-input-wrapper relative': hasPrefix || hasSuffix }">
        <div class="p-input-container">
          <div v-if="hasPrefix"
            class="absolute left-3 top-1/2 -translate-y-1/2 flex items-center z-10 w-5 justify-center">
            <slot name="prefix"></slot>
          </div>
          <div v-if="hasSuffix || (clearable && isFilled)"
            class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <div v-if="hasSuffix">
              <slot name="suffix"></slot>
            </div>
            <div v-if="clearable && isFilled">
              <i class="pi pi-times cursor-pointer" @click="clear"></i>
            </div>
          </div>
          <Textarea
            ref="inputRef"
            :id="id"
            :modelValue="fieldValue?.toString()"
            :autoResize="autoResize"
            :rows="rows"
            :cols="cols"
            :class="[
              'p-inputtext p-component w-full',
              {
                'p-filled': isFilled,
                'p-invalid': isInvalid,
                'p-disabled': disabled,
                [`p-variant-${variant}`]: variant,
                'pl-9': hasPrefix,
                'pr-9': hasSuffix || (clearable && isFilled)
              }
            ]"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :required="required"
            :maxlength="maxLength"
            :minlength="minLength"
            :aria-label="props['aria-label']"
            :aria-labelledby="props['aria-labelledby']"
            :aria-invalid="!!isInvalid"
            :aria-required="required"
            :aria-describedby="ariaDescribedBy"
            v-bind="$attrs"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            @change="onChange"
          />
        </div>
      </div>
    </div>

    <!-- Error Messages and Counter -->
    <div class="p-field-footer">
      <div v-if="showErrorMessages" class="p-error-messages">
        <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
        <small v-if="shouldShowError" :id="`${id}-error`" class="p-error block text-red-500">
          {{ getErrorMessage }}
        </small>
      </div>
      <small v-if="maxLength && showCounter" class="p-counter">
        {{ fieldValue?.toString().length || 0 }}/{{ maxLength }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Textarea from 'primevue/textarea'

export default defineComponent({
  name: 'MOPTextarea',
  components: {
    Textarea
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed,watch } from 'vue'
import { useVeeValidate } from '../../../../composables/useVeeValidate'

export interface MOPTextareaProps {
  modelValue?: string | number | null
  invalid?: boolean
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  placeholder?: string
  helperText?: string
  label?: string
  labelStyle?: 'fixed' | 'float' | 'inline'
  'aria-label'?: string
  'aria-labelledby'?: string
  id?: string
  maxLength?: number
  minLength?: number
  variant?: 'outlined' | 'filled'
  clearable?: boolean
  name: string
  hasPrefix?: boolean
  hasSuffix?: boolean
  validationType?: 'email' | 'phone' | 'password' | 'url' | 'numeric' | 'alpha' | 'alphanumeric'
  customRules?: Record<string, (value: any) => boolean | string>
  autoResize?: boolean
  rows?: number
  cols?: number
  showCounter?: boolean
}

const props = withDefaults(defineProps<MOPTextareaProps>(), {
  modelValue: null,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  label: '',
  labelStyle: 'fixed',
  'aria-label': '',
  'aria-labelledby': '',
  id: () => `mop-textarea-${Math.random().toString(36).substr(2, 9)}`,
  maxLength: undefined,
  minLength: undefined,
  variant: 'outlined',
  clearable: false,
  hasPrefix: false,
  hasSuffix: false,
  validationType: undefined,
  customRules: undefined,
  autoResize: true,
  rows: 3,
  cols: undefined,
  showCounter: false
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
  'input': [event: Event]
  'focus': [event: Event]
  'blur': [event: Event]
  'change': [event: Event]
  'clear': []
}>()

// Local State
const inputRef = ref<HTMLTextAreaElement | null>(null)
const isFocused = ref(false)
const touched = ref(false)

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty
} = useVeeValidate({
  name: props.name,
  modelValue: props.modelValue?.toString(),
  required: props.required,
  validationType: props.validationType,
  minLength: props.minLength,
  maxLength: props.maxLength,
  customRules: props.customRules
})

const isFilled = computed(() => {
  return fieldValue.value != null && fieldValue.value.toString().length > 0
})

const isInvalid = computed(() => {
  return props.invalid || shouldShowError.value
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  if (shouldShowError.value) ids.push(`${props.id}-error`)
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return !!props.helperText || shouldShowError.value
})

const getErrorMessage = computed(() => {
  if (props.required && isEmpty(fieldValue.value)) {
    return `${props.name} is required`
  }
  return errorMessage.value
})

const onInput = async (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  const value = target.value
  updateValue(value)
  emit('update:modelValue', value === '' ? null : value)
  emit('input', event)

  if (shouldShowError.value) {
    await validate()
  }
}

const onFocus = (event: Event) => {
  isFocused.value = true
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  isFocused.value = false
  touched.value = true
  emit('blur', event)
  await handleValidation()
}

const onChange = async (event: Event) => {
  emit('change', event)
  await validate()
}

const clear = () => {
  updateValue('')
  emit('update:modelValue', null)
  emit('clear')
}

const focus = () => {
  inputRef.value?.focus()
}

watch(
      () => props.modelValue,
      (newVal:any) => {
        fieldValue.value = newVal;
      }
    );

// Expose methods for parent form validation
defineExpose({
  focus,
  blur: () => inputRef.value?.blur(),
  clear,
  validate,
  errorMessage,
  isValid: computed(() => !isInvalid.value)
})
</script>

<style scoped>
.p-input-wrapper :deep(.p-inputtext) {
  padding-left: 2.5rem;
}

.p-input-wrapper :deep(.pi) {
  font-size: 1rem;
  line-height: 1;
}

.p-field-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.p-error-messages {
  flex: 1;
}

.p-counter {
  margin-left: 1rem;
  color: var(--text-color-secondary);
}

.p-float-label {
  position: relative;
}

.p-float-label label {
  position: absolute;
  top: -0.75rem;
  left: 0.75rem;
  background: var(--surface-0, #ffffff);
  padding: 0 0.25rem;
  pointer-events: none;
  transition: all 0.2s ease;
}

.p-float-label textarea:focus ~ label,
.p-float-label textarea.p-filled ~ label {
  top: -0.75rem;
  font-size: 0.875rem;
}
</style>
